import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// PwaManifestSync — applique la configuration PWA définie par le
// SuperAdmin (logo, nom, couleurs, mode d'affichage) au manifeste de
// l'application, POUR L'INSTALLATION.
//
// Le manifeste statique (public/manifest.webmanifest) sert de repli ; dès
// qu'une configuration existe en base, on génère un manifeste à la volée
// (Blob) et on remplace le <link rel="manifest">. On met aussi à jour la
// couleur de thème et l'icône Apple.
// ─────────────────────────────────────────────────────────────

const DEFAULT_ICON =
  'https://readdy.ai/api/search-image?query=Minimalist%20modern%20app%20icon%20with%20bold%20letter%20Z%20on%20warm%20neutral%20background%2C%20clean%20flat%20design%2C%20high%20contrast%2C%20centered%20composition&width=512&height=512&seq=zifek-pwa-icon-512&orientation=squarish';

const DEFAULT_SHORT_ICON_192 =
  'https://readdy.ai/api/search-image?query=Minimalist%20modern%20app%20icon%20with%20bold%20letter%20Z%20on%20warm%20neutral%20background%2C%20clean%20flat%20design%2C%20high%20contrast%2C%20centered%20composition&width=192&height=192&seq=zifek-pwa-icon-192&orientation=squarish';

interface PwaRow {
  title?: string | null;
  lienimg?: string | null;
  pwa_name?: string | null;
  pwa_short_name?: string | null;
  pwa_description?: string | null;
  pwa_logo?: string | null;
  pwa_theme_color?: string | null;
  pwa_background_color?: string | null;
  pwa_display?: string | null;
}

function iconType(url: string): string {
  const clean = url.split('?')[0].toLowerCase();
  if (clean.endsWith('.webp')) return 'image/webp';
  if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg';
  if (clean.endsWith('.svg')) return 'image/svg+xml';
  return 'image/png';
}

function upsertMeta(name: string, content: string): void {
  if (!content) return;
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function applyConfig(row: PwaRow): void {
  const logo = (row.pwa_logo || row.lienimg || DEFAULT_ICON).trim();
  const name = (row.pwa_name || row.title || 'Zifek — Tableau de bord commerçant').trim();
  const shortName = (row.pwa_short_name || 'Zifek').trim();
  const description = (row.pwa_description || 'Gérez votre commerce Zifek : caisse, produits, services, crédit et commandes.')
    .trim();
  const themeColor = (row.pwa_theme_color || '#141414').trim();
  const backgroundColor = (row.pwa_background_color || '#ffffff').trim();
  const display = (row.pwa_display || 'standalone').trim();

  const manifest = {
    name,
    short_name: shortName,
    description,
    lang: 'fr',
    dir: 'ltr',
    id: '/',
    start_url: '.',
    scope: '.',
    display,
    display_override: [display, 'minimal-ui'],
    orientation: 'any',
    background_color: backgroundColor,
    theme_color: themeColor,
    categories: ['business', 'productivity', 'finance'],
    icons: [
      { src: DEFAULT_SHORT_ICON_192, sizes: '192x192', type: 'image/jpeg', purpose: 'any' },
      { src: logo, sizes: '192x192', type: iconType(logo), purpose: 'any' },
      { src: logo, sizes: '512x512', type: iconType(logo), purpose: 'any' },
      { src: logo, sizes: '512x512', type: iconType(logo), purpose: 'maskable' },
    ],
    shortcuts: [
      { name: 'Ma caisse', short_name: 'Caisse', url: '/dashboard/caisse' },
      { name: 'Carnet de crédit', short_name: 'Crédit', url: '/dashboard/credit' },
    ],
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const url = URL.createObjectURL(blob);

  const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (link) link.setAttribute('href', url);

  const appleIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
  if (appleIcon) appleIcon.setAttribute('href', logo);

  upsertMeta('theme-color', themeColor);
  upsertMeta('apple-mobile-web-app-title', shortName);
}

export default function PwaManifestSync() {
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('zifek')
          .select('title, lienimg, pwa_name, pwa_short_name, pwa_description, pwa_logo, pwa_theme_color, pwa_background_color, pwa_display')
          .limit(1)
          .maybeSingle();
        if (!active || !data) return;
        applyConfig(data as PwaRow);
      } catch {
        /* repli : on conserve le manifeste statique */
      }
    })();
    return () => { active = false; };
  }, []);

  return null;
}